import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Car, LogOut, User, LayoutDashboard, Shield, Search, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (!q) return;
    navigate(`/auctions?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setSearchQ("");
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
              <Car className="h-5 w-5 text-white" />
            </div>
            <span className="font-extrabold text-lg tracking-tight text-slate-900">AzeAuksion<span className="text-blue-600">.az</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/auctions" data-testid="nav-auctions" className={({isActive}) => `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>Auksionlar</NavLink>
            <NavLink to="/how-it-works" data-testid="nav-how" className={({isActive}) => `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>Necə işləyir?</NavLink>
            <NavLink to="/sell" data-testid="nav-sell" className={({isActive}) => `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>Avtomobilimi sat</NavLink>
          </nav>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(v => !v)} data-testid="header-search-toggle" className="rounded-full h-10 w-10" aria-label="Axtar">
              {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </Button>
            {!user ? (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login" className="rounded-full font-semibold hidden sm:inline-flex">Giriş</Button>
                <Button onClick={() => navigate("/register")} data-testid="nav-register" className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold">Qeydiyyat</Button>
              </>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full font-semibold" data-testid="nav-user-menu">
                    <User className="h-4 w-4 mr-2" />{user.first_name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" />İdarəetmə paneli
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/sell")} data-testid="menu-sell">
                    <Car className="h-4 w-4 mr-2" />Avtomobili sat
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                      <Shield className="h-4 w-4 mr-2" />Admin panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" />Çıxış
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {searchOpen && (
          <div className="border-t border-slate-200 bg-white">
            <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2">
              <div className="flex-1 relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <Input autoFocus data-testid="header-search-input" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  placeholder="Marka, model və ya şəhər axtar..."
                  className="rounded-full h-11 pl-11 border-slate-300" />
              </div>
              <Button type="submit" data-testid="header-search-submit" className="rounded-full bg-blue-600 hover:bg-blue-700 font-semibold px-6 h-11">Axtar</Button>
            </form>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="border-t border-slate-200 bg-slate-900 text-slate-300 py-10 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Car className="h-5 w-5 text-blue-400" />
            <span className="font-bold text-white">AzeAuksion.az</span>
            <span className="text-slate-400 text-sm ml-4">© 2026 Bütün hüquqlar qorunur.</span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/how-it-works" className="text-slate-300 hover:text-white font-semibold">Necə işləyir?</Link>
            <Link to="/terms" data-testid="footer-terms" className="text-slate-300 hover:text-white font-semibold">İstifadə şərtləri</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 text-xs text-slate-400">
          Platforma avtomobilin satışını özü həyata keçirmir. Son qərarı satıcı verir.
        </div>
      </footer>
    </div>
  );
}
